import React from 'react';
import PropTypes from 'prop-types';
import {
  gql,
  useQuery,
  usePaginationHelper,
  useLazyQuery,
  useNavigate,
} from '@common/hooks';
import moment from 'moment';
import { Space } from 'antd';
import { Colors } from '@common/tokens';
import { Spacing, FontSizes } from 'works/legacy/lib/style-helper';
import { CandidateLink } from 'works/legacy/components/candidate-link';
import {
  buildOptionsFromData,
  DateRangeFilter,
  EmptyContent,
  ErrorHandler,
  FilteredTable,
  MultiSelectTableFilter,
  NumberRangeFilter,
  TextFilterSearch,
} from '@common/components';
import {
  AssignmentCancelReason,
  AssignmentStatusTag,
} from '@working-experience/components';
import { ExpiredComplianceItemsCount } from 'works/legacy/components/expired-compliance-items-count';
import { ExtensionOfferState } from 'works/legacy/components/extension-offer-state';
import { useTeamExportButton } from 'works/legacy/components/export-team-button/export-team-button-context';
import { RolesQuery } from '@clinician-profile/graphql';
import { RenderShiftType } from '@applicant-flow/components';
import { ENUM_QUERY } from '@common/graphql';
import { titleize } from '@common/services';
import { handleEmploymentTypeFilter } from '@clinician-profile/services';
import { WORKS_CLINICAL_UNITS_QUERY } from '@institutions/graphql';

const PaginatedStaffingRequestsAssignmentsQuery = gql`
  query PaginatedStaffingRequestsAssignmentsQuery(
    $limit: Int!
    $offset: Int!
    $assignmentStates: [StaffingRequestAssignmentState!]
    $orderDirection: OrderDirection
    $orderField: StaffingRequestAssignmentOrderField
    $filters: StaffingRequestAssignmentFilterInput
    $staffingRequestTypes: [StaffingRequestType!]
  ) {
    paginatedStaffingRequestAssignments(
      limit: $limit
      offset: $offset
      assignmentStates: $assignmentStates
      orderDirection: $orderDirection
      orderField: $orderField
      filters: $filters
      staffingRequestTypes: $staffingRequestTypes
    ) {
      staffingRequestAssignments {
        id
        startDate
        endDate
        cancelledReason
        cancelledDetail
        neverStarted
        state
        billingRegularRate
        lengthInWeeks
        employmentType
        organization {
          id
          name
        }
        organizationUnit {
          id
          name
          costCenter
          clinicalUnit {
            id
            displayName
          }
        }
        organizationBusiness {
          id
          name
          stateCode
          city
        }
        worksProfile {
          id
          firstName
          lastName
          fullName
          profilePic
        }
        staffingRequest {
          id
          shiftType
          hoursPerShift
          shiftsPerWeek
          code
          name
          role {
            id
            abbreviation
          }
        }
        worksList {
          id
          completedItemsCount
          totalItemsCount
        }
        ...ExpiredComplianceItemsCountFragment
        ...ExtensionOfferStateFragment
      }
      totalCount
    }
  }
  ${ExpiredComplianceItemsCount.fragment}
  ${ExtensionOfferState.fragment}
`;

const SUPPLIER_ORG_BUSINESS_QUERY = gql`
  query SupplierOrganizationBusinesses {
    organizationBusinesses {
      id
      name
      city
      stateCode

      organization {
        id
        name
      }
    }
  }
`;

export function StaffingRequestAssignmentsTable({
  filtersOverride,
  returnCount,
}) {
  const {
    filters,
    hasAnyFilter,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    filtersOverrideDefault: filtersOverride,
    localStorageFilterKey: 'SupplierTeamTable',
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'STATUS',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      name: 'NAME',
      startDate: 'START',
      endDate: 'END',
      state: 'STATUS',
      code: 'CODE',
      requestName: 'REQUEST_NAME',
      costCenter: 'COST_CENTER',
      rate: 'RATE',
      role: 'ROLE',
      shiftType: 'SHIFT_TYPE',
      shift: 'SHIFT',
      lengthInWeeks: 'LENGTH_IN_WEEKS',
      organizationBusiness: 'ORGANIZATION_BUSINESS',
      clinicalUnit: 'CLINICAL_UNIT',
      employmentType: 'EMPLOYMENT_TYPE',
    },
  });

  const navigate = useNavigate();

  const { enableExport } = useTeamExportButton();

  const [fetchOrgBusiness, { error: orgBusinesserror, data: orgBusinessData }] =
    useLazyQuery(SUPPLIER_ORG_BUSINESS_QUERY);

  const [
    fetchSupplierRoles,
    { error: supplierRolesError, data: supplierRolesData },
  ] = useLazyQuery(RolesQuery);

  const [
    fetchClinicalUnits,
    { error: clinicalUnitError, data: clinicalUnitData },
  ] = useLazyQuery(WORKS_CLINICAL_UNITS_QUERY);

  const [
    fetchEmploymentTypes,
    { error: employmentTypesError, data: employmentTypesData },
  ] = useLazyQuery(ENUM_QUERY, { variables: { key: 'EmploymentTypes' } });

  const filtersDataReady =
    !!clinicalUnitData &&
    !!supplierRolesData &&
    !!orgBusinessData &&
    !!employmentTypesData;

  const { error, data, loading } = useQuery(
    PaginatedStaffingRequestsAssignmentsQuery,
    {
      variables: {
        ...paginationVariables,
        staffingRequestTypes: ['on_demand', 'long_term'],
      },
      notifyOnNetworkStatusChange: true,
      onCompleted() {
        fetchOrgBusiness();
        fetchSupplierRoles();
        fetchClinicalUnits();
        fetchEmploymentTypes();
      },
    }
  );

  const anyError =
    error ||
    orgBusinesserror ||
    clinicalUnitError ||
    supplierRolesError ||
    employmentTypesError;

  if (anyError) {
    return (
      <ErrorHandler error={anyError} log="StaffingRequestAssignmentsTable" />
    );
  }

  const records =
    data?.paginatedStaffingRequestAssignments.staffingRequestAssignments || [];
  const total = data?.paginatedStaffingRequestAssignments.totalCount || null;

  if (returnCount) returnCount(total || 0);

  const columns = [
    {
      title: 'Team Member',
      dataIndex: 'worksProfile',
      key: 'name',
      filtered: filters.name || false,
      filteredValue: filters.name || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderTeamMemberLink(worksProfile, record) {
        return (
          <>
            <CandidateLink
              firstName={worksProfile.firstName}
              lastName={worksProfile.lastName}
              link={buildAssignmentPath(record.id)}
              subText={<ExpiredComplianceItemsCount assignment={record} />}
            />
          </>
        );
      },
    },
    {
      title: 'ID',
      dataIndex: 'staffingRequest',
      key: 'code',
      sorter: true,
      filterFormat: 'id',
      filtered: filters.code || false,
      filteredValue: filters.code || null,
      render: function renderCode(staffingRequest) {
        return staffingRequest.code;
      },
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'staffingRequest',
      key: 'requestName',
      sorter: true,
      filtered: filters.requestName || false,
      filteredValue: filters.requestName || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderRequestName(staffingRequest) {
        return staffingRequest.name;
      },
    },
    {
      title: 'CC',
      dataIndex: 'organizationUnit',
      key: 'costCenter',
      sorter: true,
      filtered: filters.costCenter || false,
      filteredValue: filters.costCenter || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderCostCenter(organizationUnit) {
        return organizationUnit.costCenter;
      },
    },
    {
      title: 'Rate',
      dataIndex: 'billingRegularRate',
      key: 'rate',
      sorter: true,
      render: function renderRate(billingRegularRate) {
        return `$${billingRegularRate.toFixed(2)}`;
      },
    },
    {
      title: 'Location',
      dataIndex: 'organizationBusiness',
      key: 'organizationBusiness',
      filtered: filters.organizationBusiness || false,
      filteredValue: filters.organizationBusiness || null,
      filters: buildOptionsFromData(
        orgBusinessData?.organizationBusinesses,
        (organizationBusiness) => ({
          value: organizationBusiness.id,
          text: organizationBusiness.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: function renderOrganizationBusiness(_, record) {
        const { organizationBusiness } = record;

        return (
          <>
            {organizationBusiness.name}
            <br />
            <span className="secondary-paragraph">
              {organizationBusiness.city}, {organizationBusiness.stateCode}
            </span>
          </>
        );
      },
      sorter: true,
    },
    {
      title: 'Unit',
      dataIndex: ['organizationUnit', 'clinicalUnit', 'displayName'],
      key: 'clinicalUnit',
      filtered: filters.clinicalUnit || false,
      filteredValue: filters.clinicalUnit || null,
      filters: buildOptionsFromData(
        clinicalUnitData?.worksClinicalUnits,
        (clinicalUnit) => ({
          value: clinicalUnit.id,
          text: clinicalUnit.displayName,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      sorter: true,
    },
    {
      title: 'Role',
      dataIndex: ['staffingRequest', 'role', 'abbreviation'],
      key: 'role',
      width: '80px',
      sorter: true,
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: buildOptionsFromData(supplierRolesData?.roles || [], (role) => ({
        value: role.id,
        text: role.name,
      })),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Employment',
      dataIndex: 'staffingRequestAssignment',
      key: 'employmentType',
      sorter: true,
      filtered: filters.employmentType || false,
      filteredValue: filters.employmentType || null,
      filters: handleEmploymentTypeFilter(employmentTypesData),
      filterDropdown: MultiSelectTableFilter,
      render: (_, record) => <>{titleize(record.employmentType)}</>,
    },
    {
      title: 'Shift Type',
      dataIndex: ['staffingRequest', 'shiftType'],
      key: 'shiftType',
      filtered: filters.shiftType || false,
      filteredValue: filters.shiftType || null,
      filterDropdown: MultiSelectTableFilter,
      filters: [
        {
          text: <RenderShiftType shiftType="day" />,
          value: 'day',
        },
        {
          text: <RenderShiftType shiftType="evening" />,
          value: 'evening',
        },
        {
          text: <RenderShiftType shiftType="night" />,
          value: 'night',
        },
        {
          text: <RenderShiftType shiftType="variable" />,
          value: 'variable',
        },
      ],
      width: '120px',
      sorter: true,
      render: (shiftType) => <RenderShiftType shiftType={shiftType} />,
    },
    {
      title: 'Shift',
      dataIndex: 'staffingRequest',
      key: 'shift',
      width: '60px',
      sorter: true,
      render: (sr) => `${sr.shiftsPerWeek}x${sr.hoursPerShift}`,
    },
    {
      title: 'Length',
      dataIndex: 'lengthInWeeks',
      key: 'lengthInWeeks',
      width: '90px',
      sorter: true,
      filtered: filters.length || false,
      filteredValue: filters.length || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <NumberRangeFilter
            onSearch={(values) => {
              values ? setSelectedKeys(values) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (length) => `${length} weeks`,
    },
    {
      title: 'Start',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: true,
      filterFormat: 'dateRange',
      filtered: filters.startDate || false,
      filteredValue: filters.startDate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <DateRangeFilter
            onSearch={(value) => {
              value ? setSelectedKeys(value) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderStartDate(_, assignment) {
        const displayDate = moment(assignment.startDate);
        return displayDate.format('MM/DD/YYYY');
      },
    },
    {
      title: 'End',
      dataIndex: 'endDate',
      sorter: true,
      key: 'endDate',
      filterFormat: 'dateRange',
      filtered: filters.endDate || false,
      filteredValue: filters.endDate || null,
      filterDropdown: ({ setSelectedKeys, confirm }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <DateRangeFilter
            onSearch={(value) => {
              value ? setSelectedKeys(value) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function renderEndDate(endDate, assignment) {
        const hasStarted = !assignment.neverStarted;
        const isCancelled = assignment.state === 'cancelled';
        const color = hasStarted && isCancelled ? Colors.coral : 'inherit';

        const displayDate = moment(endDate);
        return (
          <span style={{ color }}>{displayDate.format('MM/DD/YYYY')}</span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      sorter: true,
      defaultSortOrder: 'ascend',
      filtered: filters.state || false,
      filteredValue: filters.state || null,
      filters: [
        {
          text: 'Onboarding',
          value: 'onboarding',
        },
        {
          text: 'Cleared',
          value: 'cleared',
        },
        {
          text: 'Working',
          value: 'working',
        },
        {
          text: 'Completed',
          value: 'completed',
        },
        {
          text: 'Cancelled',
          value: 'cancelled',
        },
        {
          text: 'Extension Sent',
          value: 'extension_sent',
        },
        {
          text: 'Extended',
          value: 'extended',
        },
        {
          text: 'Extension Change Requested',
          value: 'extension_change_requested',
        },
      ],
      render: function renderState(state, record) {
        return (
          <>
            <Space size={4}>
              <AssignmentStatusTag
                status={state}
                listItemsCompleted={record.worksList?.completedItemsCount}
                listItemsTotal={record.worksList?.totalItemsCount}
                neverStarted={record.neverStarted}
              />
              <ExtensionOfferState
                assignment={record}
                offer={record.activeExtensionOffer}
                extensionCount={record.extensionCount}
                fontSize={FontSizes.small}
              />
            </Space>
            {state === 'cancelled' && (
              <div style={{ marginTop: 8 }}>
                <AssignmentCancelReason
                  reasonName={record.cancelledReason}
                  details={record.cancelledDetail}
                />
              </div>
            )}
          </>
        );
      },
    },
  ];

  if (!hasAnyFilter && !loading && records.length === 0) {
    return (
      <EmptyContent
        imagePath="./team/empty-team.png"
        header="So much empties..."
        text="Once a candidate accepts an offer, they’ll be found here where you can manage their assignments, onboarding tasks, and more."
      />
    );
  } else {
    return (
      <FilteredTable
        rowKey="id"
        columns={columns}
        dataSource={records}
        loading={loading}
        filtersDataReady={filtersDataReady}
        onRow={(record) => ({
          onClick: () => navigate(buildAssignmentPath(record.id)),
        })}
        pagination={{
          ...paginationVariables,
          total,
        }}
        onChange={tableChangeHandler}
        onFilterChange={(newFilters) => {
          filterChangeHandler(newFilters);
          enableExport();
        }}
        localStorageFilterKey={filtersOverride ? null : localStorageFilterKey}
      />
    );
  }
}

StaffingRequestAssignmentsTable.propTypes = {
  activeAssignmentId: PropTypes.string.isRequired,
  filtersOverride: PropTypes.Object,
  returnCount: PropTypes.func,
};

function buildAssignmentPath(id) {
  return `/works/supplier/team/${id}`;
}
